import { useMemo } from 'react';

import {
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  Line,
  TimeXAxis,
  YAxis,
  ReferenceArea,
  Box,
  tokens,
} from '@cast/design-system';
import { dashesOrNum, isE2E } from '@cast/utils';

import {
  buildAnalyzePrompt,
  useAnalyzeWithOpsPilot,
} from '@cast/features-organization-shared-opspilot';
import { useIsAnimationActive, HiddenDot } from 'components/chart';
import { useReactRouterStrategy } from 'components/chart/hooks';
import { attachTimeTicks } from 'utils/chart';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  RIGHTSIZING_QUERY_PARAM_KEYS,
  useRightsizingTimeRangeSelection,
} from '../../../_hooks/useRightsizingTimeRangeSelection';

type ChartPoint<TKey extends string> = { timestamp: string } & Partial<
  Record<TKey, number | null>
>;

type ChartWithPredictionProps<TKey extends string> = {
  unit?: string;
  analyzeDescription?: string;
  isLoading: boolean;
  refetch?: () => void;
  data: ChartPoint<TKey>[];
  dataKeys: readonly TKey[];
  colorMap: Record<TKey, string>;
  tooltipTitleMap: Record<TKey, string>;
  strokeDasharrayMap?: Partial<Record<TKey, string>>;
  hasError?: boolean;
  isMetricDisabled: (metric: TKey) => boolean;
  timeZone: string;
  dataKeysRight?: readonly TKey[];
  syncId?: string;
  tooltipTestId?: string;
};

const useChartWithPredictionZoomState = useReactRouterStrategy.bind(null, {
  queryParamsKeys: RIGHTSIZING_QUERY_PARAM_KEYS,
});

export const ChartWithPrediction = <TKey extends string>({
  unit,
  analyzeDescription,
  data,
  dataKeys,
  dataKeysRight,
  colorMap,
  tooltipTitleMap,
  strokeDasharrayMap,
  isLoading,
  hasError,
  refetch = window.location.reload,
  isMetricDisabled,
  timeZone,
  syncId = 'workload-metrics',
  tooltipTestId = 'data-popper',
}: ChartWithPredictionProps<TKey>) => {
  const { cluster } = useCluster();
  const { getRange } = useRightsizingTimeRangeSelection();
  const rightKeys = dataKeysRight ?? ([] as TKey[]);
  const chartData = useMemo(() => attachTimeTicks(data), [data]);
  const isAnimationActive = useIsAnimationActive(chartData);
  const firstPredictionUnixTimestamp = useMemo(
    () =>
      chartData.find((item) => new Date(item.timestamp) > new Date())
        ?.chartTimestampUnix,
    [chartData]
  );

  const analyzeWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: analyzeDescription ?? '',
      scopeName: cluster.name,
      getDefaultRange: getRange,
    }),
  });

  return (
    <Box
      position="relative"
      height="100%"
      display="flex"
      flexDirection="column"
      justifyContent="center"
    >
      {!hasError && (
        <ChartContainer
          data={chartData}
          isLoading={isLoading}
          sx={{ width: '100%' }}
          zoomProps={{
            xAxisDataKey: 'chartTimestampUnix',
            useZoomState: useChartWithPredictionZoomState,
          }}
          chartActionsSx={{
            top: 0,
            // align with the 2nd y-axis
            right: rightKeys.length > 0 ? 4 : 0,
          }}
          syncId={syncId}
          analyzeWithOpsPilot={analyzeWithOpsPilot}
        >
          {/* Reuse gradient definition */}
          <defs>
            <linearGradient id="highlightGradient2" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={tokens.theme.data.indigo100}
                stopOpacity="0"
              />
              <stop
                offset="100%"
                stopColor={tokens.theme.data.indigo100}
                stopOpacity="1"
              />
            </linearGradient>
          </defs>

          <ChartGrid hideVertical yAxisId={0} />

          <TimeXAxis unixDataKey="chartTimestampUnix" timezone={timeZone} />

          {dataKeys.map((dataKey) => {
            const hasData = data.some((d) => d[dataKey] != null);
            if (!hasData || isMetricDisabled(dataKey)) return null;

            const lineUnit = rightKeys.includes(dataKey) ? '%' : unit;
            return (
              <Line<ChartPoint<TKey>>
                key={dataKey}
                dataKey={dataKey}
                stroke={colorMap[dataKey]}
                strokeWidth={1.5}
                strokeDasharray={strokeDasharrayMap?.[dataKey]}
                tooltipConfig={{
                  displayName: tooltipTitleMap[dataKey],
                  ...(lineUnit ? { unit: lineUnit } : {}),
                }}
                dot={isE2E() ? HiddenDot : false}
                isAnimationActive={isAnimationActive}
                yAxisId={rightKeys.includes(dataKey) ? 1 : 0}
              />
            );
          })}

          <YAxis
            allowDecimals={true}
            unitFormatter={(v) => (unit ? `${v} ${unit}` : String(v))}
            yAxisId={0}
          />

          {rightKeys.length > 0 && (
            <YAxis
              allowDecimals={true}
              orientation="right"
              padding={{ top: 0 }}
              yAxisId={1}
              ticks={[0, 25, 50, 75, 100]}
              domain={[0, 120]}
              tickFormatter={(t) => ` ${t}%`}
            />
          )}

          {/* Prediction area highlight */}
          {firstPredictionUnixTimestamp && (
            <ReferenceArea
              strokeOpacity={0.3}
              fill="url(#highlightGradient2)"
              stroke={tokens.theme.data.indigo100}
              strokeWidth={1}
              x1={firstPredictionUnixTimestamp}
            />
          )}

          <ChartTooltip<ChartPoint<TKey>>
            header={(props) => (
              <ChartTooltipTimestampHeader {...props} timezone={timeZone} />
            )}
            formatValue={(value) => dashesOrNum({ value, roundBy: 3 })}
            testId={tooltipTestId}
          />
        </ChartContainer>
      )}
      {hasError && <FailedToLoad refresh={refetch} />}
    </Box>
  );
};
